import { useCallback, useEffect, useState } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Building2, Pencil, Trash2, Wifi, WifiOff, Clock3 } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, tone, testid }) {
  const tones = { live: "text-emerald-600", off: "text-red-600", pending: "text-amber-600", base: "text-slate-900" };
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-heading text-2xl font-semibold mt-0.5 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function Pill({ n, tone, icon: Icon, testid }) {
  const styles = n === 0 ? "bg-slate-100 text-slate-400"
    : tone === "live" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${styles}`} data-testid={testid}>
      {tone === "live" && n > 0 && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />}
      <Icon className="h-3.5 w-3.5" />{n}
    </span>
  );
}

function RenameDialog({ biz, onClose, onDone }) {
  const [name, setName] = useState(biz?.name || "");
  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await apiAdmin.patch(`/businesses/${biz.id}`, { name: name.trim() });
      toast.success("Nombre actualizado");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="biz-rename-dialog">
        <DialogHeader><DialogTitle>Cambiar nombre del negocio</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="biz-rename-input" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="biz-rename-cancel">Cancelar</Button>
            <Button type="submit" disabled={!name.trim()} data-testid="biz-rename-save">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ biz, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await apiAdmin.delete(`/businesses/${biz.id}`);
      toast.success(`Negocio eliminado. ${data.released_tvs} TV(s) volvieron a Pendientes.`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="biz-delete-dialog">
        <DialogHeader>
          <DialogTitle>¿Eliminar “{biz.name}”?</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Se borrarán su contenido, listas, horarios y Video Walls. Sus {biz.tvs_total} TV(s) no se borran:
            vuelven a <b>Pendientes</b> para asignarlas a otro negocio. Esto no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="text-xs text-slate-500">Escribe <b>ELIMINAR</b> para confirmar</label>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" data-testid="biz-delete-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="biz-delete-cancel">Cancelar</Button>
          <Button onClick={remove} disabled={confirm.trim().toUpperCase() !== "ELIMINAR" || busy} className="bg-red-600 hover:bg-red-700" data-testid="biz-delete-confirm">
            {busy ? "Eliminando…" : "Eliminar negocio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BusinessesOverview() {
  const { activeBusiness, setActiveBusiness, businesses, refreshBusinesses } = useBusiness();
  const [data, setData] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await apiAdmin.get("/businesses/overview");
      setData(d);
    } catch { /* keep last snapshot */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const done = async () => { setRenaming(null); setDeleting(null); await Promise.all([load(), refreshBusinesses()]); };
  const t = data?.totals;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white" data-testid="biz-overview">
      <div className="flex flex-wrap items-end justify-between gap-4 px-6 pt-5 pb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Mis negocios</div>
          <div className="font-heading text-lg font-semibold text-slate-900">Estado de pantallas por negocio</div>
          <p className="text-xs text-slate-500 mt-0.5">“En vivo” = se comunicó en los últimos 2 minutos. Se actualiza cada 15 s.</p>
        </div>
      </div>
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-6 pb-5">
          <Stat label="Negocios" value={t.businesses} tone="base" testid="biz-total-businesses" />
          <Stat label="TVs" value={t.tvs_total} tone="base" testid="biz-total-tvs" />
          <Stat label="En vivo" value={t.tvs_live} tone="live" testid="biz-total-live" />
          <Stat label="Sin señal" value={t.tvs_offline} tone="off" testid="biz-total-offline" />
          <Stat label="Pendientes" value={t.pending} tone="pending" testid="biz-total-pending" />
        </div>
      )}
      <div className="overflow-x-auto border-t border-slate-100">
        <table className="w-full text-sm" data-testid="biz-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
              <th className="px-6 py-3 font-medium">Negocio</th>
              <th className="px-4 py-3 font-medium text-center">TVs</th>
              <th className="px-4 py-3 font-medium text-center">En vivo</th>
              <th className="px-4 py-3 font-medium text-center">Sin señal</th>
              <th className="px-6 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-400">Cargando…</td></tr>}
            {data?.businesses.map((b) => {
              const isActive = activeBusiness?.id === b.id;
              return (
                <tr key={b.id} className="border-t border-slate-100 transition-colors duration-200 hover:bg-slate-50/70" data-testid={`biz-row-${b.id}`}>
                  <td className="px-6 py-3.5">
                    <button
                      type="button"
                      onClick={() => setActiveBusiness(businesses.find((x) => x.id === b.id) || b)}
                      className="flex items-center gap-3 text-left"
                      data-testid={`biz-select-${b.id}`}
                    >
                      <span className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center ${isActive ? "bg-[#4C5BD8] text-white" : "bg-slate-100 text-slate-500"}`}>
                        <Building2 className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block font-medium text-slate-900" data-testid={`biz-name-${b.id}`}>{b.name}</span>
                        {isActive && <span className="text-[11px] text-[#4C5BD8]">Seleccionado</span>}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3.5 text-center font-semibold text-slate-800" data-testid={`biz-tvs-${b.id}`}>{b.tvs_total}</td>
                  <td className="px-4 py-3.5 text-center"><Pill n={b.tvs_live} tone="live" icon={Wifi} testid={`biz-live-${b.id}`} /></td>
                  <td className="px-4 py-3.5 text-center"><Pill n={b.tvs_offline} tone="off" icon={WifiOff} testid={`biz-offline-${b.id}`} /></td>
                  <td className="px-6 py-3.5">
                    <div className="flex justify-end gap-1">
                      <button type="button" onClick={() => setRenaming(b)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors" aria-label="Cambiar nombre" data-testid={`biz-rename-${b.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => setDeleting(b)} className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label="Eliminar negocio" data-testid={`biz-delete-${b.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {t?.pending > 0 && (
        <div className="flex items-center gap-2 border-t border-slate-100 px-6 py-3 text-xs text-amber-700">
          <Clock3 className="h-3.5 w-3.5" /> {t.pending} Fire Stick(s) esperando aprobación en Devices.
        </div>
      )}
      {renaming && <RenameDialog biz={renaming} onClose={() => setRenaming(null)} onDone={done} />}
      {deleting && <DeleteDialog biz={deleting} onClose={() => setDeleting(null)} onDone={done} />}
    </section>
  );
}
